
    // IMPORTS
    import { computed, defineComponent, PropType } from 'vue'
    import { buildLocalization, StringUtil, useFieldValidation } from '@/util'

    import { Input } from '@progress/kendo-vue-inputs'
    import { Error, Hint } from '@progress/kendo-vue-labels'

    const PHONE_REGEX = /^\+\d[\d\s]*$/

    export default defineComponent({
        name: 'PhoneField',

        components: {
            'k-input': Input,
            Error,
            Hint
        },

        props: {
            id: { type: String as PropType<string>, required: true },
            modelValue: { type: String as PropType<string>, required: true },
            required: { type: Boolean as PropType<boolean>, required: false, default: () => false },
        },

        emits: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            'update:modelValue': (value: string) => true,
        },

        setup(props, { emit }) {
            /********************************************************************************************************************
             STATE
             ********************************************************************************************************************/
            const { localize } = buildLocalization()

            const { showError, errorMessage, clearError } = useFieldValidation(props.id, () => {
                const value = props.modelValue
                const isEmpty = !StringUtil.hasContent(value)
                if (props.required && isEmpty) return localize('requiredField')
                if (!isEmpty && !PHONE_REGEX.test(value)) return localize('invalidMobileNo')
                return null
            })

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const computedLabel = computed(() => props.required
                ? localize('mobileNumber')
                : `${localize('mobileNumber')} (${localize('optional')})`
            )

            const computedData = { localize, computedLabel, errorMessage, showError }

            /********************************************************************************************************************
             HELPER METHODS
             ********************************************************************************************************************/
            function sanitize(raw: string): string {
                // Keep '+' only at position 0; elsewhere only digits and spaces
                return raw
                    .split('')
                    .filter((ch, i) => (i === 0 && ch === '+') || /[\d\s]/.test(ch))
                    .join('')
            }

            /********************************************************************************************************************
             EVENT HANDLERS
             ********************************************************************************************************************/
            function onFocus() {
                if (props.modelValue === '') {
                    emit('update:modelValue', '+')
                }
            }

            function onBlur() {
                if (props.modelValue === '+') {
                    emit('update:modelValue', '')
                }
            }

            function onInput(e: { value: string }) {
                clearError()
                emit('update:modelValue', sanitize(e.value))
            }

            function onKeyDown(e: KeyboardEvent) {
                // Prevent typing '+' anywhere other than position 0
                if (e.key === '+' && (e.target as HTMLInputElement).selectionStart !== 0) {
                    e.preventDefault()
                }
            }

            const publicMethods = { onFocus, onBlur, onInput, onKeyDown }

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...computedData, ...publicMethods }
        }
    })
